import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { settingsAPI } from '../../services/settingsAPI';
import { toast } from 'react-hot-toast';

const InlineTextArea = ({ 
  children, 
  settingKey, 
  defaultValue = '', 
  className = '',
  onSave = null,
  placeholder = 'Cliquez pour éditer...',
  rows = 3
}) => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const textareaRef = useRef(null);
  const originalValue = useRef('');

  // Initialize value when component mounts or children change
  useEffect(() => {
    const currentValue = children || defaultValue;
    setValue(currentValue);
    originalValue.current = currentValue;
  }, [children, defaultValue]);

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [isEditing]);

  const handleDoubleClick = () => {
    if (!isAdmin) return;
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!settingKey || value === originalValue.current) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      // Get JWT token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      await settingsAPI.update(settingKey, value, token);
      originalValue.current = value;
      setIsEditing(false);
      
      // Update the children prop to reflect the new value immediately
      if (typeof children === 'string') {
        // Force re-render by updating the parent's state
        // This will trigger a re-render and show the new value
        window.dispatchEvent(new CustomEvent('settingUpdated', {
          detail: { key: settingKey, value: value }
        }));
      }
      
      // Call custom onSave callback if provided
      if (onSave) {
        onSave(value);
      }
      
      toast.success('Modification enregistrée !');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error(error.message || 'Erreur lors de la sauvegarde');
      setValue(originalValue.current); // Revert to original value
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setValue(originalValue.current);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleBlur = () => {
    if (value !== originalValue.current) {
      handleSave();
    } else {
      setIsEditing(false);
    }
  };

  if (!isAdmin) {
    return <span className={className}>{children}</span>;
  }

  if (isEditing) {
    return (
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={placeholder}
          rows={rows}
          className={`${className} w-full px-3 py-2 border-2 border-blue-500 rounded-md bg-white text-gray-900 focus:outline-none focus:border-blue-600 resize-none`}
          disabled={isSaving}
        />
        <div className="absolute -top-8 right-0 text-xs text-gray-600 bg-white px-2 py-1 rounded border">
          Ctrl+Entrée pour sauvegarder, Échap pour annuler
        </div>
        {isSaving && (
          <div className="absolute -top-6 right-0 text-xs text-blue-600">
            Sauvegarde...
          </div>
        )}
      </div>
    );
  }

  return (
    <span
      className={`${className} ${isAdmin ? 'cursor-pointer hover:bg-blue-50 hover:border-2 hover:border-blue-300 transition-all duration-200 px-2 py-1 rounded block' : ''}`}
      onDoubleClick={handleDoubleClick}
      title={isAdmin ? 'Double-cliquez pour modifier' : ''}
    >
      {children}
      {isAdmin && (
        <span className="ml-2 text-xs text-gray-400 opacity-0 hover:opacity-100 transition-opacity">
          ✏️
        </span>
      )}
    </span>
  );
};

export default InlineTextArea;
